import type { ComponentType } from "react";
import {
  IconClipboardList,
  IconCoin,
  IconCreditCard,
  IconFileText,
  IconInbox,
  IconLockOpen,
  IconMessage,
  IconShieldCheck,
  IconShieldX,
  IconSparkles,
  IconUserCheck,
  IconUserX,
  IconUsers,
} from "@tabler/icons-react";
import type { UserNotificationType } from "@shared/notifications";

export const NOTIFICATION_NAVY = "#04305c";
export const NOTIFICATION_UNREAD_BG = "var(--mantine-color-blue-0)";
export const NOTIFICATION_READ_BG = "#ffffff";
export const NOTIFICATION_BORDER_COLOR = "var(--mantine-color-gray-2)";
export const NOTIFICATION_UNREAD_DOT = "var(--mantine-color-blue-5)";

type NotificationIconProps = { size?: number; stroke?: number; color?: string };

export type NotificationIconConfig = {
  icon: ComponentType<NotificationIconProps>;
  iconColor: string;
  circleBg: string;
};

export function getNotificationIconConfig(type: UserNotificationType): NotificationIconConfig {
  switch (type) {
    case "CHAT_MESSAGE":
      return {
        icon: IconMessage,
        iconColor: "var(--mantine-color-blue-6)",
        circleBg: "var(--mantine-color-blue-0)",
      };
    case "DOCUMENT_SHARED":
      return {
        icon: IconFileText,
        iconColor: "var(--mantine-color-green-6)",
        circleBg: "var(--mantine-color-green-0)",
      };
    case "VERIFICATION_APPROVED":
      return {
        icon: IconShieldCheck,
        iconColor: "var(--mantine-color-teal-6)",
        circleBg: "var(--mantine-color-teal-0)",
      };
    case "VERIFICATION_REJECTED":
      return {
        icon: IconShieldX,
        iconColor: "var(--mantine-color-orange-6)",
        circleBg: "var(--mantine-color-orange-0)",
      };
    case "REQUEST_RECEIVED":
    case "REQUEST_ACCEPTED":
    case "REQUEST_DECLINED":
      return {
        icon: IconInbox,
        iconColor: "var(--mantine-color-blue-6)",
        circleBg: "var(--mantine-color-blue-0)",
      };
    case "PARTNER_PAYMENT":
    case "SENDER_PAYMENT_REQUIRED":
    case "SENDER_PAYMENT_FINAL_REMINDER":
      return {
        icon: IconCreditCard,
        iconColor: "var(--mantine-color-violet-6)",
        circleBg: "var(--mantine-color-violet-0)",
      };
    case "CREDITS_RESTORED_TIMEOUT":
    case "FREE_CREDITS_EXPIRING_SOON":
    case "FREE_CREDITS_EXPIRED":
      return {
        icon: IconCoin,
        iconColor: "var(--mantine-color-yellow-7)",
        circleBg: "var(--mantine-color-yellow-0)",
      };
    case "WORKSPACE_UNLOCKED":
    case "DISCOVERY_UNLOCKED":
      return {
        icon: IconLockOpen,
        iconColor: "var(--mantine-color-blue-6)",
        circleBg: "var(--mantine-color-blue-0)",
      };
    case "WELCOME_FIRST_LOGIN":
    case "WELCOME_RETURNING":
      return {
        icon: IconSparkles,
        iconColor: "var(--mantine-color-blue-6)",
        circleBg: "var(--mantine-color-blue-0)",
      };
    case "FACILITATOR_ASSIGNMENT_ACCEPTED":
      return {
        icon: IconUserCheck,
        iconColor: "var(--mantine-color-teal-6)",
        circleBg: "var(--mantine-color-teal-0)",
      };
    case "FACILITATOR_ASSIGNMENT_REJECTED":
      return {
        icon: IconUserX,
        iconColor: "var(--mantine-color-orange-6)",
        circleBg: "var(--mantine-color-orange-0)",
      };
    case "FACILITATOR_ASSIGNMENT_REQUEST_RECEIVED":
    case "FACILITATOR_PARTNER_RECOMMENDED":
    case "ECOSYSTEM_HEAD_JOIN_REQUEST":
    case "ECOSYSTEM_HEAD_PARTNER_RECOMMENDED":
    case "ECOSYSTEM_HEAD_COLLAB_REVIEW_RECEIVED":
    case "ECOSYSTEM_HEAD_COLLAB_REVIEW_QUEUED":
    case "ECOSYSTEM_HEAD_COLLAB_REVIEW_FORWARDED":
    case "ECOSYSTEM_HEAD_MEETING_SCHEDULED":
      return {
        icon: IconInbox,
        iconColor: "var(--mantine-color-blue-6)",
        circleBg: "var(--mantine-color-blue-0)",
      };
    case "ECOSYSTEM_HEAD_JOIN_ACCEPTED":
    case "ECOSYSTEM_HEAD_VERIFICATION_APPROVED":
      return {
        icon: IconShieldCheck,
        iconColor: "var(--mantine-color-teal-6)",
        circleBg: "var(--mantine-color-teal-0)",
      };
    case "ECOSYSTEM_HEAD_JOIN_REJECTED":
    case "ECOSYSTEM_HEAD_VERIFICATION_REJECTED":
    case "ECOSYSTEM_HEAD_COMPANY_REMOVED":
    case "ECOSYSTEM_HEAD_COLLAB_REVIEW_REJECTED":
      return {
        icon: IconShieldX,
        iconColor: "var(--mantine-color-orange-6)",
        circleBg: "var(--mantine-color-orange-0)",
      };
    case "ECOSYSTEM_HEAD_QUESTIONNAIRE_SUBMITTED":
    case "ECOSYSTEM_HEAD_MORE_INFO_SUBMITTED":
    case "ECOSYSTEM_HEAD_MORE_INFO_REQUESTED":
      return {
        icon: IconClipboardList,
        iconColor: "var(--mantine-color-violet-6)",
        circleBg: "var(--mantine-color-violet-0)",
      };
    default:
      return {
        icon: IconUsers,
        iconColor: "var(--mantine-color-blue-6)",
        circleBg: "var(--mantine-color-blue-0)",
      };
  }
}

export function formatRelativeTime(iso: string) {
  const d = new Date(iso);
  const diffMs = Date.now() - d.getTime();
  const mins = Math.floor(diffMs / 60000);
  if (mins < 1) return "Just now";
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d ago`;
  return d.toLocaleDateString();
}
